/**
 * FeedComposer — Facebook-style "Create post" entry box.
 *
 * The user's avatar + a prompt button + Video / Link / Content / Photo actions
 * that open the shared FeedShareModal. Used on the main feed, community/org
 * feeds, and the Project Journal so all four surfaces share one post UI.
 *
 * Target resolution:
 *   • `communityGuid` given  → posts to that community/org feed.
 *   • `onSubmitShare` given  → custom persist (Project Journal posts).
 *   • neither (main feed)    → posts to the user's home community (fetched).
 *
 * Permission gating lives with the caller (it renders FeedComposer only when the
 * viewer may post — community membership for feeds, `canShare` for the Journal).
 *
 * Photo posting is DISABLED ("Soon") until the media-moderation pipeline ships
 * (CSAM + AI classifier + EXIF/GPS strip + NCMEC). See outputs/EPIC_Media_Phase.md.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import { getDeclaredCommunityGuid } from '../../Data/store';
import { mediaSrc } from '../../api/mediaUrl';
import FeedShareModal, { type ShareSubmitPayload } from '../CommunityNewsFeed/FeedShareModal';
import type { ShareType } from '../../api/feedShare';
import styles from './FeedComposer.module.css';

interface Props {
  /** Called after a successful share so the surface can refresh.
   *  `firstPost` = true exactly once, on the user's first-ever feed
   *  share (DEV-1441 Voice moment). */
  onShared?: (firstPost?: boolean) => void;
  /** Explicit target community/org feed. Omit on the main feed (uses home community). */
  communityGuid?: string;
  /** Custom persist (Project Journal) — overrides the community-share path. */
  onSubmitShare?: (payload: ShareSubmitPayload) => Promise<void>;
  /** Label for the content tab (Journal passes "My Content"). */
  contentTabLabel?: string;
  /** Allow text-only posts (default true). Journal passes false. */
  allowText?: boolean;
  /** Modal header (defaults to "Create post"). */
  modalTitle?: string;
}

export const FeedComposer: React.FC<Props> = ({
  onShared, communityGuid, onSubmitShare, contentTabLabel, allowText = true, modalTitle,
}) => {
  const { t } = useAppTranslation('pages');
  const { user } = useAuth();

  // Main feed = no explicit target and no custom submit → post to home community.
  const isMainFeed = !communityGuid && !onSubmitShare;

  const [homeCommunity, setHomeCommunity] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<ShareType | undefined>(undefined);
  const [showNoHome, setShowNoHome] = useState(false);

  // DEV-1372 slice 1 — home community resolves from the active
  // type='home' community_memberships row (the single source of truth),
  // not the security_users column. The store hydrates asynchronously and
  // dispatches l4e_community_membership_changed; subscribe so the value
  // appears without a reload.
  useEffect(() => {
    if (!isMainFeed) return;
    const resolve = () =>
      setHomeCommunity(getDeclaredCommunityGuid(String(user?.userGuid || ''), 'home'));
    resolve();
    window.addEventListener('l4e_community_membership_changed', resolve);
    return () => window.removeEventListener('l4e_community_membership_changed', resolve);
  }, [isMainFeed, user?.userGuid]);

  // Where the default (community-share) path posts. Unused when onSubmitShare set.
  const target = communityGuid ?? homeCommunity ?? undefined;

  const firstName = (user?.displayName ?? '').trim().split(/\s+/)[0] || '';
  const promptText = firstName
    ? t('feed.composer.prompt', { name: firstName })
    : t('feed.composer.promptNoName');

  const openWith = useCallback((tab?: ShareType) => {
    // Only the main feed can lack a target (no home community set yet).
    if (!onSubmitShare && !target) { setShowNoHome(true); return; }
    setShowNoHome(false);
    setInitialTab(tab);
    setOpen(true);
  }, [onSubmitShare, target]);

  const canOpen = !!onSubmitShare || !!target;

  return (
    <div className={styles.composer}>
      <div className={styles.topRow}>
        {user?.profileImageUrl ? (
          <img className={styles.avatar} src={mediaSrc(user.profileImageUrl) ?? undefined} alt="" />
        ) : (
          <div className={styles.avatarFallback} aria-hidden>
            {firstName.charAt(0).toUpperCase() || '·'}
          </div>
        )}
        <button type="button" className={styles.prompt} onClick={() => openWith()}>
          {promptText}
        </button>
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.action} onClick={() => openWith('video')}>
          <span className={styles.actionIcon} aria-hidden>🎥</span>
          <span className={styles.actionLabel}>{t('feed.composer.video')}</span>
        </button>
        <button type="button" className={styles.action} onClick={() => openWith('link')}>
          <span className={styles.actionIcon} aria-hidden>🔗</span>
          <span className={styles.actionLabel}>{t('feed.composer.link')}</span>
        </button>
        <button type="button" className={styles.action} onClick={() => openWith('chronicle')}>
          <span className={styles.actionIcon} aria-hidden>📄</span>
          <span className={styles.actionLabel}>{contentTabLabel ?? t('feed.composer.content')}</span>
        </button>
        {/* Photo disabled until the media-moderation pipeline ships. */}
        <button
          type="button"
          className={`${styles.action} ${styles.actionDisabled}`}
          disabled
          title={t('feed.composer.photoSoon')}
        >
          <span className={styles.actionIcon} aria-hidden>📷</span>
          <span className={styles.actionLabel}>{t('feed.composer.photo')}</span>
          <span className={styles.soonBadge}>{t('feed.composer.soon')}</span>
        </button>
      </div>

      {showNoHome && (
        <div className={styles.noHome}>{t('feed.composer.noHome')}</div>
      )}

      {canOpen && (
        <FeedShareModal
          communityGuid={onSubmitShare ? undefined : target}
          onSubmitShare={onSubmitShare}
          allowText={allowText}
          contentTabLabel={contentTabLabel}
          isOpen={open}
          initialShareType={initialTab}
          headerTitle={modalTitle ?? t('feed.composer.createPost')}
          onClose={() => setOpen(false)}
          onShared={(firstPost) => { setOpen(false); onShared?.(firstPost); }}
        />
      )}
    </div>
  );
};

export default FeedComposer;
